import React, { useState, useEffect, useRef, useMemo, useContext } from 'react';
import { DrawerItem } from '../TGDrawer/TGDrawer';
import { Box, Theme, Input, InputAdornment, alpha, createStyles, makeStyles, Typography } from '@material-ui/core';
import { colors } from '../../styles/theme';
import { BuildingType, FloorPlanType } from '../../contexts/AggregatesContext/types';
import { KeyboardArrowRightOutlined, Search} from '@material-ui/icons';
import { AssetsDashboardContext } from '../../contexts/assetsDashboardContext';
import { useCommonStyles } from '../Map/component/ControlsOverlay/VisibilityButton/useStyles';
import { hasIncludes } from '../../utils/utilityHelper';
import _ from 'lodash';
import useUserPreferences from '../../hooks/useUserPreferences';

export interface FloorPlanSectionProps {
  buildings: Array<BuildingType>;
  floorPlans: Array<FloorPlanType>;
  selectedFloorPlan: FloorPlanType | null;
  setSelectedFloorPlan: (floorPlan: FloorPlanType | null) => void;
  onClick?: (floorPlan: FloorPlanType) => void;
  theme?: Theme;
  translateFunction?: (key: string, params?: Record<string, string | number | boolean>) => string;
}

const BUILDING_PREFERENCE_KEY = 'openBuildings';
const FLOOR_PLAN_PREFERENCE_KEY = 'openFloorPlans';

const FloorPlanSection: React.FC<FloorPlanSectionProps> = (props) => {
  const { buildings, floorPlans, selectedFloorPlan, setSelectedFloorPlan, onClick, translateFunction = (key: string) => key } = props;
  const { isMobileView } = useContext(AssetsDashboardContext);
  const classes = useStyles({ isMobileView: isMobileView }); 
  const commonClasses = useCommonStyles();
  const { getPreference, setPreference } = useUserPreferences();
  const [openBuildings, setOpenBuildings] = useState<Record<string, boolean>>(getPreference(BUILDING_PREFERENCE_KEY) || {});
  const [openFloorPlans, setOpenFloorPlans] = useState<Record<string, boolean>>(getPreference(FLOOR_PLAN_PREFERENCE_KEY) ||{});
  const [canFocusOut, setCanFocusOut] = useState(false);
  const [searchText, setSearchText] = useState('');
  const inputRef = useRef<null | HTMLInputElement>(null);

  useEffect(() => {
    setPreference(BUILDING_PREFERENCE_KEY, openBuildings);
  }, [openBuildings, setPreference]);

  useEffect(() => {
    setPreference(FLOOR_PLAN_PREFERENCE_KEY, openFloorPlans);
  }, [openFloorPlans, setPreference]);

  const handleFloorPlanClick = (selectedFloorPlan: FloorPlanType) => {
    if (onClick) {
      return onClick(selectedFloorPlan);
    } 
    return setSelectedFloorPlan(selectedFloorPlan);
  };

  function filterFloorPlan(floorPlan: FloorPlanType, searchText: string): FloorPlanType | undefined {
    const titleMatches = hasIncludes(floorPlan.label, searchText);
    const buildingMatches = hasIncludes(floorPlan.building?.label, searchText);
  
    const filteredSubFloorPlans = (floorPlan.subFloorPlans || [])
      .map(sub => {
        const fullSubFloorPlan = floorPlans.find(fp => fp.id === sub.id);
        if (fullSubFloorPlan) {
          return filterFloorPlan(fullSubFloorPlan, searchText);
        }
        return undefined;
      })
      .filter(Boolean) as FloorPlanType[];
  
    const hasMatchingDescendants = filteredSubFloorPlans.length > 0;
    if (titleMatches || buildingMatches || hasMatchingDescendants) {
      return {
        ...floorPlan,
        subFloorPlans: filteredSubFloorPlans,
      };
    }
  
    return undefined;
  }
  
  const sortedFloorPlans = useMemo(() => { 
    if (searchText && searchText !== '') {
      let filteredFloorPlans = [...floorPlans].map(fp => filterFloorPlan(fp, searchText)).filter(Boolean);
      if (filteredFloorPlans.length > 0) {
        return (filteredFloorPlans as FloorPlanType[]).sort((a, b) => a.title.localeCompare(b.title));
      } else {
        return [];
      }
    }
    return [...floorPlans].sort((a, b) => a.title.localeCompare(b.title));
  }, [searchText, floorPlans]);

  function filterBuilding(building: BuildingType, searchText: string): BuildingType | undefined {
    const filteredTitle = hasIncludes(building.label, searchText);
    const filteredFloorPlans = building.floorPlans.filter(fp => hasIncludes(fp.label, searchText));
    if (filteredTitle || filteredFloorPlans.length > 0) {
      return {
        ...building,
        label: building.label,
        floorPlans: filteredFloorPlans.map(fp => ({
          ...fp,
          subFloorPlans: (fp?.subFloorPlans || []).filter(sub => hasIncludes(sub.label, searchText)),
        })),
      };
    }
    return undefined;
  }

  const sortedBuildings = useMemo(() => {
    if (searchText && searchText !== '') {
      let filteredBuildings = [...buildings].map(building => filterBuilding(building, searchText)).filter(Boolean);
      if (filteredBuildings.length > 0) {
        return (filteredBuildings as BuildingType[]).sort((a, b) => a.title.localeCompare(b.title));
      } else {
        return [];
      }
    }
    return [...buildings].sort((a, b) => a.title.localeCompare(b.title));
  }, [searchText, buildings]);

  const RenderSearchBar = (searchValue: string, focusOut: boolean, searchOnChange: (value: string) => void) => {

    useEffect(() => {
      if (focusOut) {
        inputRef.current?.blur();
      }
    }, [focusOut]);

    return (
      <>
        <Box className={classes.searchContainer}>
          <Input 
            className={classes.searchbar}
            inputRef={inputRef}
            startAdornment={
              <InputAdornment position="start">
                <Search className={classes.searchIcon} />
              </InputAdornment>
            }
            disableUnderline
            placeholder={translateFunction('Search')}
            value={searchValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              searchOnChange(e.target.value);
            }}
          />
        </Box>
      </>
    );
  };
  
  const RenderFloorPlan = ({ floorPlans, level }: { floorPlans: FloorPlanType[]; level: number }) => {  
    return (
      <>
        {floorPlans.map((floorPlan) => {
          const subFloorPlans = sortedFloorPlans.filter(
            sub => sub.baseFloorPlan?.id === floorPlan.id
          );
    
          const isExpanded = openFloorPlans[floorPlan.id] ?? false;
    
          const toggleExpand = (e: React.MouseEvent) => {
            e.stopPropagation();
            setOpenFloorPlans(prev => ({
              ...prev,
              [floorPlan.id]: !prev[floorPlan.id],
            }));
          };
          
          return (
            <div key={floorPlan.id}>
              <Box
                onClick={() => handleFloorPlanClick(floorPlan)}
                className={`${selectedFloorPlan?.id === floorPlan.id ? classes.selectedFloorPlan : ''} ${classes.floorPlan}`}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    paddingLeft: `${level * 16}px`,
                  }}
                >
                  {subFloorPlans.length > 0 && (
                    <KeyboardArrowRightOutlined
                      className={classes.expandIcon}
                      onClick={toggleExpand}
                      style={{ transform: isExpanded ? 'rotate(90deg)' : 'none', transition: '0.2s' }}
                    />
                  )}
                  <Box 
                    sx={{paddingLeft: subFloorPlans.length > 0 ? '0px' : '10px'}}
                  >
                    <Typography variant="body1" className={classes.floorPlanTitleContainer}>
                      {floorPlan.label}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              {isExpanded && subFloorPlans.length > 0 && (
                <RenderFloorPlan floorPlans={subFloorPlans} level={level + 1} />
              )}
            </div>
          );
        })}
      </>
    );
  };

  return (
    <Box className={commonClasses.container} >
      {RenderSearchBar(searchText, canFocusOut, setSearchText)}
      <Box style={{ minHeight: '450px', maxHeight: '450px', overflowY: 'auto' }}>
        <Box className={commonClasses.detailContainer} sx={{ padding: '10px 16px !important' }} onScroll={() => setCanFocusOut(true)}>
          {sortedBuildings.map((building) => {
            const buildingLabel = building.label;                      
            const baseFloorPlans = sortedFloorPlans.filter(fp => fp.buildingId === building.id && !fp.baseFloorPlan);
            const isExpanded = openBuildings[building.id] ?? true;

            const toggleExpand = (e: React.MouseEvent) => {
              e.stopPropagation(); 
              setOpenBuildings(prev => ({
                ...prev,
                [building.id]: !prev[building.id],
              }));
            };

            return (
              <div key={building.id}>
                <Box className={classes.building}>
                  <KeyboardArrowRightOutlined
                      className={classes.expandIcon}
                      onClick={toggleExpand}
                      style={{ transform: isExpanded ? 'rotate(90deg)' : 'none', transition: '0.2s' }}
                    />
                  <Box className={classes.buildingTitleContainer}>{buildingLabel}</Box>
                </Box>
                {isExpanded && (
                  <RenderFloorPlan floorPlans={baseFloorPlans} level={0} />
                )}
              </div>
            );
          })}
          {_.isEmpty(sortedBuildings) && (
            <>
              <Typography className={classes.emptyText}>{translateFunction('No Floor Plans Available')}</Typography>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export const floorPlanSection = (props: FloorPlanSectionProps): DrawerItem => {
  return {
    title: 'Floor Plans',
    content: <FloorPlanSection {...props} />,
  };
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    expandIcon: {
      color: colors.title,
      paddingRight: '2px',
      cursor: 'pointer',
    },
    building: {
      display: 'flex',
      alignItems: 'center',
      cursor: 'pointer',
      padding: '6px 0',
    },
    emptyText: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      textAlign: 'center',
      height: '100%',
      alignItems: 'center',
      paddingTop: '175px',
    },
    buildingTitleContainer: {
      display: 'flex',
      alignItems: 'center',
      fontWeight: 450,
    },
    floorPlanTitleContainer: {
      fontSize: 14,
      fontWeight: 400,
    },
    searchContainer: {
      zIndex: 10,
      padding: '10px 20px',
      borderBottom: `1px solid ${colors.black10}`,
      borderRadius: 4,
    },
    searchbar: ({ isMobileView }: { isMobileView: boolean }) => ({
      padding: '8px 12px',
      backgroundColor: colors.cultured7,
      fontSize: isMobileView ? 16 : 14,
      fontWeight: 400,
      lineHeight: 'normal',
      color: colors.black0,
      borderRadius: 4,
      '&::placeholder': {
        color: colors.black35,
      },
      width: '100%',
      '& .MuiInputBase-input': {
        padding: '2px 0',
      },
    }),
    searchIcon: {
      color: colors.black35,
      height: 22,
      width: 22,
    },
    selectedFloorPlan: {
      backgroundColor: alpha(theme.palette.primary.main, 0.1),
      color: theme.palette.primary.main,
    },  
    floorPlan: ({ isMobileView }: { isMobileView: boolean }) => ({
      display: 'flex',
      alignItems: 'center',
      cursor: 'pointer',
      padding: '8px 16px',
      marginRight: '6px',
      borderRadius: 1,
      ...(isMobileView
        ? {} 
        : {
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
            },
          }),
    })
  })
);
